import type { ApiErrorBody, ApiErrorCode, AuthResponse } from '@multicrm/shared';

/**
 * Клиент gateway.
 *
 * Токен доступа живёт только в памяти — из localStorage его мог бы прочитать
 * любой скрипт на странице. В хранилище лежит лишь одноразовый refresh-токен:
 * при обмене он сразу заменяется новым, а повторное использование старого
 * gateway расценивает как утечку и закрывает все сессии.
 */

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');
const REFRESH_STORAGE_KEY = 'multicrm.refresh';
/** Обновляем токен заранее: иначе первый запрос после простоя всегда ловит 401. */
const REFRESH_MARGIN_MS = 30_000;

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Доступ закрыт насовсем — перелогин не поможет. */
  get isAccessDenied(): boolean {
    return this.code === 'telegram_id_not_allowed' || this.code === 'forbidden';
  }

  get isAuthProblem(): boolean {
    return (
      this.code === 'unauthorized' ||
      this.code === 'session_revoked' ||
      this.code === 'invalid_init_data'
    );
  }
}

/** localStorage в WebView может быть недоступен — тогда работаем из памяти. */
const memoryStorage = new Map<string, string>();

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key) ?? memoryStorage.get(key) ?? null;
  } catch {
    return memoryStorage.get(key) ?? null;
  }
}

function writeStorage(key: string, value: string | null): void {
  if (value === null) {
    memoryStorage.delete(key);
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ничего страшного: память уже обновлена
    }
    return;
  }
  memoryStorage.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ничего страшного: память уже обновлена
  }
}

interface SessionState {
  accessToken: string | null;
  accessExpiresAt: number;
  initDataRaw: string | null;
}

const session: SessionState = {
  accessToken: null,
  accessExpiresAt: 0,
  initDataRaw: null,
};

let refreshing: Promise<void> | null = null;
const authListeners = new Set<(reason: ApiErrorCode) => void>();

/** Подписка на «сессия потеряна»: приложение покажет экран входа. */
export function onAuthLost(listener: (reason: ApiErrorCode) => void): () => void {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
}

function loseAuth(reason: ApiErrorCode): void {
  session.accessToken = null;
  session.accessExpiresAt = 0;
  writeStorage(REFRESH_STORAGE_KEY, null);
  for (const listener of authListeners) listener(reason);
}

export function rememberInitData(initDataRaw: string | null): void {
  session.initDataRaw = initDataRaw;
}

function applyAuth(auth: AuthResponse): AuthResponse {
  session.accessToken = auth.accessToken;
  session.accessExpiresAt = Date.now() + auth.expiresIn * 1000;
  writeStorage(REFRESH_STORAGE_KEY, auth.refreshToken);
  return auth;
}

export function hasStoredSession(): boolean {
  return readStorage(REFRESH_STORAGE_KEY) !== null;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = null;
  }
  return new ApiError(
    body?.error.code ?? 'upstream_error',
    body?.error.message ?? `Помилка ${response.status}`,
    response.status,
    body?.error.details,
    body?.error.requestId,
  );
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

/** Вход по данным запуска Telegram. */
export async function authenticate(initDataRaw: string): Promise<AuthResponse> {
  session.initDataRaw = initDataRaw;
  return applyAuth(await post<AuthResponse>('/api/auth/telegram', { initData: initDataRaw }));
}

/**
 * Продлить сессию. Сначала refresh-токеном, а если он не принят — заново по
 * initData: внутри Telegram строка запуска всё время под рукой, поэтому
 * пользователь не видит ни одного экрана входа.
 */
async function renewSession(): Promise<void> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const refreshToken = readStorage(REFRESH_STORAGE_KEY);
    if (refreshToken) {
      try {
        applyAuth(await post<AuthResponse>('/api/auth/refresh', { refreshToken }));
        return;
      } catch (error) {
        if (error instanceof ApiError && error.isAccessDenied) {
          loseAuth(error.code);
          throw error;
        }
        writeStorage(REFRESH_STORAGE_KEY, null);
      }
    }

    if (session.initDataRaw) {
      try {
        await authenticate(session.initDataRaw);
        return;
      } catch (error) {
        loseAuth(error instanceof ApiError ? error.code : 'unauthorized');
        throw error;
      }
    }

    loseAuth('unauthorized');
    throw new ApiError('unauthorized', 'Потрібен повторний вхід', 401);
  })();

  try {
    await refreshing;
  } finally {
    refreshing = null;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

/** Запрос к защищённой части API: токен подставляется и продлевается сам. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!session.accessToken || Date.now() > session.accessExpiresAt - REFRESH_MARGIN_MS) {
    await renewSession();
  }

  const url = new URL(`${API_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }

  const send = async (): Promise<Response> =>
    fetch(url, {
      method: options.method ?? 'GET',
      headers: {
        Authorization: `Bearer ${session.accessToken ?? ''}`,
        ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });

  let response = await send();

  if (response.status === 401) {
    // Токен мог истечь между проверкой и отправкой: одна честная попытка.
    const failure = await parseError(response);
    if (failure.isAccessDenied) throw failure;
    await renewSession();
    response = await send();
  }

  if (!response.ok) {
    const error = await parseError(response);
    if (error.isAccessDenied) loseAuth(error.code);
    throw error;
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function logout(): Promise<void> {
  try {
    await api('/api/auth/logout', { method: 'POST' });
  } catch {
    // Выходим локально даже если сервер не ответил.
  }
  loseAuth('unauthorized');
}

/**
 * Адрес потока событий. Токен в строке запроса не передаём — сначала берём
 * одноразовый тикет, потому что URL попадает в логи прокси.
 */
export async function eventStreamUrl(lastSeq: number): Promise<string> {
  const { ticket } = await api<{ ticket: string; expiresIn: number }>('/api/events/ticket', {
    method: 'POST',
  });
  const url = new URL(`${API_URL}/events`, window.location.origin);
  url.searchParams.set('ticket', ticket);
  if (lastSeq > 0) url.searchParams.set('lastSeq', String(lastSeq));
  return url.toString();
}
